import React from 'react';

import { APPOINTMENTS, statusColor } from '../../constants';
import { dayName, daysUntil, monthDay } from '../../helpers';
import AppointmentCard from '../AppointmentCard';
import ReadinessDetail from '../ReadinessDetail';
import TaskDetail from '../TaskDetail';
import styles from './ReadinessView.module.scss';

const ReadinessView = ({
  apptsByDate,
  readinessStats,
  selectedAppt,
  taskFromReadiness,
  setSelectedAppt,
  setTaskFromReadiness,
}) => {
  const hasApptsByDateProp = apptsByDate != null;
  const safeApptsByDate = apptsByDate ?? {};
  const safeStats = readinessStats ?? { green: 0, yellow: 0, red: 0 };
  const totalAppointments = hasApptsByDateProp
    ? Object.values(safeApptsByDate).reduce((sum, list) => sum + list.length, 0)
    : APPOINTMENTS.length;

  const sortedDates = Object.keys(safeApptsByDate).sort((a, b) => {
    const aTime = new Date(a).getTime();
    const bTime = new Date(b).getTime();
    const aValid = Number.isFinite(aTime);
    const bValid = Number.isFinite(bTime);
    if (aValid && bValid) return aTime - bTime;
    if (aValid) return -1;
    if (bValid) return 1;
    return a.localeCompare(b);
  });

  const stats = [
    { key: 'upcoming', label: 'Upcoming 99483s', value: totalAppointments, sub: 'visits' },
    { key: 'ready', label: 'Ready', value: safeStats.green, color: 'green' },
    { key: 'risk', label: 'At Risk', value: safeStats.yellow, color: 'yellow' },
    { key: 'not-ready', label: 'Not Ready', value: safeStats.red, color: 'red' },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.statsRow}>
        {stats.map((stat) => (
          <div key={stat.key} className={styles.statCard}>
            <div className={styles.statLabel}>{stat.label}</div>
            <div className={styles.statValueRow}>
              <span
                className={styles.statValue}
                style={{ color: stat.color ? statusColor[stat.color] : '#18181B' }}
              >
                {stat.value}
              </span>
              {stat.sub && <span className={styles.statSub}>{stat.sub}</span>}
            </div>
          </div>
        ))}
      </div>

      <div
        className={`${styles.layout} ${
          selectedAppt || taskFromReadiness ? styles.layoutWithDetail : ''
        }`}
      >
        <div className={styles.listColumn}>
          {sortedDates.map((date) => {
            const appointments = safeApptsByDate[date] || [];
            const dateObj = new Date(date);
            const isValidDate = Number.isFinite(dateObj.getTime());
            const dayOffset = isValidDate ? daysUntil(date) : null;
            const isToday = dayOffset === 0;
            const dayLabel = isValidDate ? dayName(date) : '—';
            const dayNumber = isValidDate ? dateObj.getDate() : '—';
            const monthLabel = isValidDate ? monthDay(date) : '—';

            return (
              <div key={date} className={styles.dateGroup}>
                <div className={styles.dateHeader}>
                  <div className={styles.dateLeft}>
                    <div
                      className={`${styles.dateDayName} ${
                        isToday ? styles.dateToday : styles.dateMuted
                      }`}
                    >
                      {dayLabel}
                    </div>
                    <div
                      className={`${styles.dateDayNumber} ${
                        isToday ? styles.dateToday : styles.dateNormal
                      }`}
                    >
                      {dayNumber}
                    </div>
                  </div>
                  <div className={styles.dateDivider} />
                  <span className={styles.dateMeta}>
                    {monthLabel}
                    {isToday ? ' · Today' : ''}
                  </span>
                </div>
                <div className={styles.cardsGrid}>
                  {appointments.map((appt) => (
                    <AppointmentCard
                      key={appt.id}
                      appt={appt}
                      isSelected={selectedAppt?.id === appt.id}
                      onClick={() => {
                        setSelectedAppt(appt);
                        setTaskFromReadiness(null);
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {(selectedAppt || taskFromReadiness) && (
          <div className={styles.detailPanel}>
            {taskFromReadiness ? (
              <TaskDetail task={taskFromReadiness} onClose={() => setTaskFromReadiness(null)} />
            ) : (
              <ReadinessDetail
                appt={selectedAppt}
                onClose={() => setSelectedAppt(null)}
                onSelectTask={setTaskFromReadiness}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadinessView;
